import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RefundService } from '../../services/refunds';
import { Refund } from '../../types';
import { safeArray } from '../../utils/money';

// 异步actions
export const fetchRefunds = createAsyncThunk(
  'refund/fetchRefunds',
  async (params: { page?: number; limit?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await RefundService.listRefunds(
        params.page,
        params.limit,
        params.filters
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取退款列表失败');
    }
  }
);

export const fetchRefundDetail = createAsyncThunk(
  'refund/fetchDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await RefundService.getRefund(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取退款详情失败');
    }
  }
);

export const createRefund = createAsyncThunk(
  'refund/create',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await RefundService.createRefund(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建退款失败');
    }
  }
);

interface RefundState {
  refunds: {
    data: Refund[];
    meta: any;
    loading: boolean;
  };
  currentRefund: Refund | null;
  creating: boolean;
  error: string | null;
}

const initialState: RefundState = {
  refunds: {
    data: [],
    meta: null,
    loading: false,
  },
  currentRefund: null,
  creating: false,
  error: null,
};

const refundSlice = createSlice({
  name: 'refund',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentRefund: (state) => {
      state.currentRefund = null;
    },
  },
  extraReducers: (builder) => {
    // 获取退款列表
    builder
      .addCase(fetchRefunds.pending, (state) => {
        state.refunds.loading = true;
        state.error = null;
      })
      .addCase(fetchRefunds.fulfilled, (state, action) => {
        state.refunds.loading = false;
        state.refunds.data = safeArray<Refund>(action.payload.data);
        state.refunds.meta = action.payload.meta;
      })
      .addCase(fetchRefunds.rejected, (state, action) => {
        state.refunds.loading = false;
        state.error = action.payload as string;
      });

    // 获取退款详情
    builder
      .addCase(fetchRefundDetail.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchRefundDetail.fulfilled, (state, action) => {
        state.currentRefund = action.payload;
      })
      .addCase(fetchRefundDetail.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // 创建退款
    builder
      .addCase(createRefund.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createRefund.fulfilled, (state, action) => {
        state.creating = false;
        state.refunds.data.unshift(action.payload);
      })
      .addCase(createRefund.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentRefund } = refundSlice.actions;
export default refundSlice.reducer;
